import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Download, Loader2, Eye, BookOpen } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function DocRow({ d, checked, active, onToggle, onView }) {
  return (
    <li
      className={`flex items-start gap-2 px-3 py-2 border-b border-rule cursor-pointer transition-colors ${active ? "bg-[var(--u-navy-wash)]" : "hover:bg-[#F7FAFC]"}`}
      onClick={() => onView(d)}
      data-testid={`doc-row-${d.num}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(d.num)}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 accent-[#1C4E80]"
        data-testid={`doc-check-${d.num}`}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-semibold text-navy leading-tight">
          <span className="text-coral font-mono mr-1">{d.num}</span>{d.title}
        </div>
        <div className="text-[10.5px] text-slate leading-tight mt-0.5">{d.audience}</div>
      </div>
      <Eye size={13} className={`mt-0.5 shrink-0 ${active ? "text-navy" : "text-slate"}`} />
    </li>
  );
}

// Platform Admin: browse, view and download the fixed-snapshot technical documentation set.
export default function DocumentationModal({ onClose }) {
  const [docs, setDocs] = useState(null);
  const [issued, setIssued] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [viewing, setViewing] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/docs`).then((r) => {
      setDocs(r.data.documents);
      setIssued(r.data.issued);
      if (r.data.documents[0]) setViewing(r.data.documents[0]);
    }).catch(() => setDocs([]));
  }, []);

  useEffect(() => {
    if (!viewing) return undefined;
    let url;
    setPdfUrl(null);
    axios.get(`${API}/admin/docs/${viewing.num}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setPdfUrl(url); })
      .catch(() => toast.error("Couldn't load that document."));
    return () => url && URL.revokeObjectURL(url);
  }, [viewing]);

  const toggle = (num) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(num)) n.delete(num); else n.add(num);
    return n;
  });
  const allOn = docs && docs.length > 0 && selected.size === docs.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(docs.map((d) => d.num)));

  const download = async () => {
    const picked = docs.filter((d) => selected.has(d.num));
    if (!picked.length) return;
    setBusy(true);
    try {
      if (picked.length === 1) {
        const r = await axios.get(`${API}/admin/docs/${picked[0].num}`, { responseType: "blob" });
        saveBlob(r.data, picked[0].filename);
      } else {
        const r = await axios.get(`${API}/admin/docs/bundle`, { params: { nums: picked.map((d) => d.num).join(",") }, responseType: "blob" });
        saveBlob(r.data, "uReserve_Technical_Documentation.zip");
      }
    } catch {
      toast.error("Download failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose} data-testid="documentation-modal">
      <div className="bg-white rounded-[3px] shadow-xl flex flex-col w-full max-w-[1320px] h-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2" style={{ background: "#1C4E80", color: "#fff" }}>
          <span className="font-display font-bold text-[15px] flex items-center gap-2"><BookOpen size={15} /> Technical Documentation</span>
          <span className="text-[11px] opacity-80" data-testid="documentation-issued">Date issued: {issued}</span>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/[0.12] rounded-[3px]" data-testid="documentation-close"><X size={16} /></button>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="w-[330px] shrink-0 flex flex-col border-r border-rule">
            <div className="flex items-center justify-between px-3 py-2 border-b border-rule bg-[var(--u-navy-wash)]">
              <label className="flex items-center gap-2 text-[11.5px] font-semibold text-navy cursor-pointer">
                <input type="checkbox" checked={!!allOn} onChange={toggleAll} className="accent-[#1C4E80]" data-testid="doc-check-all" />
                Select all
              </label>
              <button
                type="button"
                disabled={!selected.size || busy}
                onClick={download}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-[3px] bg-navy text-white hover:opacity-90 disabled:opacity-40"
                data-testid="doc-download-selected"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                Download{selected.size ? ` (${selected.size})` : ""}
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto" data-testid="doc-list">
              {docs === null && <li className="px-3 py-3 text-[12px] text-slate">Loading…</li>}
              {docs?.map((d) => (
                <DocRow key={d.num} d={d} checked={selected.has(d.num)} active={viewing?.num === d.num} onToggle={toggle} onView={setViewing} />
              ))}
            </ul>
            <div className="px-3 py-2 text-[10.5px] text-slate border-t border-rule">One file downloads as PDF; several download together as a .zip.</div>
          </div>
          <div className="flex-1 min-w-0 bg-[#EEF2F6]">
            {pdfUrl ? (
              <iframe title={viewing?.title} src={pdfUrl} className="w-full h-full border-0" data-testid="doc-viewer" />
            ) : (
              <div className="h-full flex items-center justify-center text-[12px] text-slate gap-2"><Loader2 size={14} className="animate-spin" /> Loading document…</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
